<script setup lang='ts'>
import Slider from '@vueform/slider';
import { useDebounceFn } from '@vueuse/core';
import { onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useModeListData from '@/composables/useModeListData';
import useModeListFilters from '@/composables/useModeListFilters';
import useResponsive from '@/composables/useResponsive';

const props = defineProps<{
  min: number;
  max: number;
  step: number;
}>();
const { t } = useI18n();
const { isMobile } = useResponsive();
const { filters, saveFiltersToLocalStorage, loadFiltersFromLocalStorage } = useModeListFilters();
const { fetchProductsList, getBaseMetrics } = useModeListData();

const FILTER_TYPE = 'sessionDuration';
const METRIC_NAME = 'sessionDurationWithSelection';

const values = ref<Array<number>>([props.min, props.max]);
const baseMin = ref(0);
const baseMax = ref(24);

loadFiltersFromLocalStorage();
const savedFilter = ref(filters.value.selections.find(it => it.filterProperties.type === FILTER_TYPE)?.selection);
if (savedFilter.value) {
  values.value = [savedFilter.value.min / 60, savedFilter.value.max / 60];
}

async function setupBaseData() {
  const baseFilters = { ...filters.value };
  baseFilters.selections = baseFilters.selections.filter((it: any) => it.filterProperties.type !== FILTER_TYPE);
  const baseMetrics = await getBaseMetrics(baseFilters);
  const baseDurationMetric = baseMetrics.find((it: any) => it.metricName === METRIC_NAME);
  if (baseDurationMetric) {
    baseMin.value = baseDurationMetric.value.min / 60;
    baseMax.value = baseDurationMetric.value.max / 60;
    if (!savedFilter.value) {
      values.value = [baseMin.value, baseMax.value];
    }
  }
}

async function setupDuration(val: Array<number>) {
  const filterIndex = filters.value.selections.findIndex(it => it.filterProperties.type === FILTER_TYPE);
  if (filterIndex > -1)
    filters.value.selections.splice(filterIndex, 1);

  const isDefaultDuration = val[0] === baseMin.value && val[1] === baseMax.value;
  if (!isDefaultDuration) {
    filters.value.selections.push({ filterProperties: { type: FILTER_TYPE }, selection: { min: val[0] * 60, max: val[1] * 60 } });
  }
  saveFiltersToLocalStorage();
  await fetchProductsList(filters.value);
}

const debounceSetupDuration = useDebounceFn((val: Array<number>) => {
  setupDuration(val);
}, 100);

onBeforeMount(async () => {
  await setupBaseData();
});
</script>

<template>
  <div class="slider">
    <Slider v-model="values" :min="baseMin" :max="baseMax" :step="props.step" :tooltips="false" @change="(v: number[]) => debounceSetupDuration(v)" />
  </div>
  <div class="inputs-wrapper">
    <div class="left" style="display: flex; flex-direction: column;">
      <p class="label">
        {{ t('menu.start') }}
      </p>
      <input v-model="values[0]" type="number" class="input left" :aria-label="t('menu.start')" :min="baseMin" :max="baseMax" :step="props.step" :disabled="isMobile" @change="debounceSetupDuration(values)">
    </div>
    <div class="right" style="display: flex; flex-direction: column;">
      <p class="label">
        {{ t('menu.end') }}
      </p>
      <input v-model="values[1]" type="number" class="input right" :aria-label="t('menu.end')" :min="baseMin" :max="baseMax" :step="props.step" :disabled="isMobile" @change="debounceSetupDuration(values)">
    </div>
  </div>
</template>

<style lang="css" scoped>
.infos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--yb-spacing-base);

  .hour-range {
    font-size: var(--yb-text-sm);

    span {
      font-weight: var(--yb-text-bold);
    }
  }

  button {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background-color: var(--yb-color-white);
    color: var(--yb-color-black);
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: var(--yb-radius-sm);
    outline-color: var(--yb-outline);
    cursor: pointer;

    &:hover {
      background-color: var(--yb-color-lightgray);
    }

    &:focus-visible,
    &:focus-within,
    &:focus {
      outline: 2px solid var(--yb-color-primary);
    }
  }
}

.slider {
  min-width: 280px;
  margin-top: var(--yb-spacing-base);
  --slider-height: 2px;
  --slider-handle-bg: var(--yb-color-white);
  --slider-handle-radius: var(--yb-radius-sm);
  --slider-connect-bg: var(--yb-color-primary);
  --slider-tooltip-bg: var(--yb-color-primary);
  --slider-handle-shadow: 0.5px 0.5px 2px 1px var(--yb-color-mediumgray);
  --slider-handle-shadow-active: 0.5px 0.5px 2px 1px var(--yb-color-mediumgray);
  --slider-handle-ring-width: 2px;
  --slider-handle-ring-color: var(--yb-color-lightgray);
}

.inputs-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--yb-spacing-xl);

  .left,
  .right {
    display: flex;
    flex-direction: column;
  }

  .left {
    align-items: flex-start;
  }

  .right {
    align-items: flex-end;
  }

  .input {
    max-width: 70px;
    font-size: var(--yb-text-sm);
    font-weight: var(--yb-text-bold);
    padding: var(--yb-spacing-xs);
    border-radius: var(--yb-radius-sm);
    background-color: var(--yb-color-white);
    color: var(--yb-color-black);
    border: 1px solid var(--yb-color-mediumgray);

    &:focus-visible,
    &:focus-within,
    &:focus {
      outline: 1px solid var(--yb-color-primary);
    }

    &:disabled {
      background-color: var(--yb-color-lightgray);
    }
  }

  .label {
    font-size: var(--yb-text-xs);
    margin-bottom: 4px;
  }
}
</style>
